
















































































import { computed, defineComponent, PropType, ref, watch, nextTick, toRefs } from '@vue/composition-api';
import treeNodeHoverEstimate from '@/components/common/tree-node-hover-estimate/index.vue';
import treeNodeHoverEstimatePopover from '@/components/common/tree-node-hover-estimate-popover/index.vue';
import { AdgroupFormData, InterestKeywordInfo, InterestOption, InterestSearchResult } from '@tt4b/ttam-enums';
import { exposure } from 'byted-vui/src/directives/exposure';
import { differenceBy } from 'lodash-es';
import { useRecommendInterestKeywordTrack } from '../hooks/track';

const name = 'ttam-interests-common-interests-category';

export default defineComponent({
  name,
  components: {
    treeNodeHoverEstimate,
    treeNodeHoverEstimatePopover,
  },
  directives: { exposure },
  props: {
    value: {
      type: Array as PropType<number[]>,
      required: true,
    },
    options: {
      type: Array as PropType<InterestOption[]>,
      required: true,
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      required: true,
      default: () => ({}),
    },
    loading: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    filterMethod: {
      type: Function as PropType<(node: unknown) => boolean>,
      default: () => () => true,
    },
    recommendKeywords: {
      type: Array as PropType<InterestSearchResult[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { adgroupData } = toRefs(props);
    const adgroupId = computed(() => adgroupData.value?.adId || '');
    const _recommendKeywords = ref(props.recommendKeywords);
    const _value = computed({
      get: () => props.value,
      set: v => {
        emit('change', v);
        emit('input', v);
      },
    });
    const onSearchKeywordChange = keyword => {
      emit('search', keyword);
    };
    const onSelectTreeChange = v => {
      emit('change', v);
    };
    const treeNodeHoverEstimateRef = ref();

    function onMouseEnterNode(node) {
      emit('node-hover');
      const hoverNodeData = node?.data ?? {};
      treeNodeHoverEstimateRef.value?.showHoverEstimate(hoverNodeData);
    }

    function onCloseOne() {
      emit('close-one');
    }

    const recommendedTagsContainerRef = ref<HTMLElement | null>(null);
    const hasWrapInRecommendedTagsContainer = ref(true);
    const hasExpandedRecommendedTagsContainer = ref(false);
    const { updateRecommendTraceInfo } = useRecommendInterestKeywordTrack({
      adgroupData,
    });

    function detectRecommendedKeywordsWrap() {
      if (!recommendedTagsContainerRef.value) {
        return;
      }

      hasWrapInRecommendedTagsContainer.value =
        recommendedTagsContainerRef.value.scrollHeight > recommendedTagsContainerRef.value.offsetHeight;
    }
    function selectRecommendation(interestKeyword, indexInRecommendation) {
      const interestKeywordsI18n = [...adgroupData.value?.interestKeywordsI18n];
      const interestKeywordsLangI18n = [...adgroupData.value?.interestKeywordsLangI18n];

      const { keyword_id, lang } = interestKeyword;

      updateRecommendTraceInfo([keyword_id]);

      if (!interestKeywordsI18n.includes(keyword_id)) {
        interestKeywordsI18n.push(keyword_id);
        interestKeywordsLangI18n.push(lang);
      }
      emit('interest-keywords-i18n-change', interestKeywordsI18n);
      emit('interest-keywords-lang-i18n-change', interestKeywordsLangI18n);
      _recommendKeywords?.value?.splice?.(indexInRecommendation, 1);
    }

    function selectAllRecommendations() {
      const interestKeywordsI18n = [...adgroupData.value?.interestKeywordsI18n];
      const { interestKeywordsLangI18n } = adgroupData.value;

      const selectableKeywords = differenceBy(
        _recommendKeywords.value,
        interestKeywordsI18n,
        item => (item as InterestKeywordInfo).keyword_id || item,
      );

      const selectableKeywordIds = selectableKeywords.map(({ keyword_id }) => keyword_id);

      updateRecommendTraceInfo(selectableKeywordIds);

      emit('interest-keywords-i18n-change', [...interestKeywordsI18n, ...selectableKeywordIds]);
      emit('interest-keywords-lang-i18n-change', [
        ...interestKeywordsLangI18n,
        ...selectableKeywords.map(o => (o as any).lang),
      ]);
      _recommendKeywords.value = [];
    }

    watch(
      () => props.recommendKeywords,
      newVal => {
        _recommendKeywords.value = newVal;
      },
    );

    watch(_recommendKeywords, () => nextTick(detectRecommendedKeywordsWrap));

    return {
      name,
      adgroupId,
      _value,
      treeNodeHoverEstimateRef,
      onMouseEnterNode,
      onCloseOne,
      onSelectTreeChange,
      onSearchKeywordChange,
      detectRecommendedKeywordsWrap,
      selectRecommendation,
      selectAllRecommendations,
      recommendedTagsContainerRef,
      hasExpandedRecommendedTagsContainer,
      _recommendKeywords,
      hasWrapInRecommendedTagsContainer,
    };
  },
});
